import type { BudgetCategory, BudgetMonth, CategoryGroup } from './types'

/**
 * Pure, side-effect-free YNAB-envelope math.
 *
 * MIRROR of `Budget/Services/BudgetEngine.swift`. Until WEB-63 lets us
 * share the implementation across platforms, the contract is:
 *
 *   1. iOS BudgetEngine and this file MUST produce identical
 *      `BudgetMonth` outputs given the same inputs.
 *   2. A change to one side requires the matching change to the
 *      other in the same MR (parity ledger entry).
 *   3. Unit tests on both sides use the same fixtures (TBD).
 *
 * The "available" semantics here are:
 *   - Category.available = the persisted, user-facing balance
 *     (carryOver + assigned + activity). YNAB-imported docs hold the
 *     prior-month leftover inside `available` with carryOver 0, so
 *     writers MUST update available by delta (never recompute it from
 *     assigned + activity, which would destroy the leftover).
 *   - BudgetMonth.available = carryOver + income - sum(assigned)
 *     = "ready to assign" / "to be budgeted". carryOver is the prior
 *     month's ready-to-assign rolled forward (YNAB's TBA is
 *     cumulative across the life of the budget, not month-local).
 *
 * activity is negative for outflows and positive for inflows; assigned
 * is always >= 0.
 */

function categoryAvailable(assigned: number, activity: number): number {
  return assigned + activity
}

function sumAssigned(month: BudgetMonth): number {
  return month.categoryGroups.reduce(
    (acc, g) => acc + g.categories.reduce((a, c) => a + c.assigned, 0),
    0,
  )
}

function sumActivity(month: BudgetMonth): number {
  return month.categoryGroups.reduce(
    (acc, g) => acc + g.categories.reduce((a, c) => a + c.activity, 0),
    0,
  )
}

function readyToAssign(carryOver: number, income: number, totalAssigned: number): number {
  return carryOver + income - totalAssigned
}

function withCategoryUpdate(
  month: BudgetMonth,
  categoryId: string,
  update: (c: BudgetCategory) => BudgetCategory,
): BudgetMonth {
  const groups: CategoryGroup[] = month.categoryGroups.map((g) => ({
    ...g,
    categories: g.categories.map((c) => (c.id === categoryId ? update(c) : c)),
  }))
  const totalAssigned = groups.reduce(
    (acc, g) => acc + g.categories.reduce((a, c) => a + c.assigned, 0),
    0,
  )
  return {
    ...month,
    categoryGroups: groups,
    budgeted: totalAssigned,
    available: readyToAssign(month.carryOver ?? 0, month.income, totalAssigned),
  }
}

export type CalcError =
  | { kind: 'negativeAssignment' }
  | { kind: 'invalidAmount' }
  | { kind: 'insufficientFunds'; available: number; requested: number }
  | { kind: 'categoryNotFound'; id: string }

export type CalcResult<T> = { ok: true; value: T } | { ok: false; error: CalcError }

/**
 * Set a category's `assigned` to an absolute amount. This is the
 * common UI op — typing into a cell sets the value, not a delta.
 *
 * The iOS engine has a delta-style `assignMoney` (add `amount` to
 * existing); the UI normalizes both into "set to X" by computing the
 * delta first. Doing the set form here keeps the web flow cleaner
 * for inline-edit. The end state of the BudgetMonth is identical.
 */
export function setAssigned(
  month: BudgetMonth,
  categoryId: string,
  newAssigned: number,
): CalcResult<BudgetMonth> {
  if (newAssigned < 0) {
    return { ok: false, error: { kind: 'negativeAssignment' } }
  }
  const found = findCategory(month, categoryId)
  if (!found) {
    return { ok: false, error: { kind: 'categoryNotFound', id: categoryId } }
  }
  const updated = withCategoryUpdate(month, categoryId, (c) => {
    const next: BudgetCategory = {
      ...c,
      assigned: newAssigned,
      available: Math.round((c.available + (newAssigned - c.assigned)) * 100) / 100,
    }
    // Re-derive targetUnderfunded so the "Fund to goal" / "Fund all"
    // buttons stop firing once the gap is covered.
    return { ...next, targetUnderfunded: computeUnderfunded(next, month.year, month.month) }
  })
  return { ok: true, value: updated }
}

/**
 * Move money between two categories. `null` for either side means
 * "to/from To-Be-Assigned" (i.e. it adjusts only one category's
 * assigned amount, increasing or decreasing ready-to-assign).
 */
export function moveMoney(
  month: BudgetMonth,
  fromId: string | null,
  toId: string | null,
  amount: number,
): CalcResult<BudgetMonth> {
  if (amount <= 0) return { ok: false, error: { kind: 'invalidAmount' } }
  if (fromId === toId) return { ok: false, error: { kind: 'invalidAmount' } }

  let working = month

  if (fromId !== null) {
    const from = findCategory(month, fromId)
    if (!from) return { ok: false, error: { kind: 'categoryNotFound', id: fromId } }
    const result = setAssigned(working, fromId, from.assigned - amount)
    if (!result.ok) return result
    working = result.value
  }

  if (toId !== null) {
    const to = findCategory(working, toId)
    if (!to) return { ok: false, error: { kind: 'categoryNotFound', id: toId } }
    const result = setAssigned(working, toId, to.assigned + amount)
    if (!result.ok) return result
    working = result.value
  }

  return { ok: true, value: working }
}

export function findCategory(month: BudgetMonth, id: string): BudgetCategory | undefined {
  for (const g of month.categoryGroups) {
    const c = g.categories.find((c) => c.id === id)
    if (c) return c
  }
  return undefined
}

export function groupTotals(group: CategoryGroup): {
  assigned: number
  activity: number
  available: number
} {
  return group.categories.reduce(
    (acc, c) => ({
      assigned: acc.assigned + c.assigned,
      activity: acc.activity + c.activity,
      available: acc.available + (c.available || categoryAvailable(c.assigned, c.activity)),
    }),
    { assigned: 0, activity: 0, available: 0 },
  )
}

export function monthTotals(month: BudgetMonth): {
  assigned: number
  activity: number
  toBeAssigned: number
} {
  const assigned = sumAssigned(month)
  return {
    assigned,
    activity: sumActivity(month),
    toBeAssigned: readyToAssign(month.carryOver ?? 0, month.income, assigned),
  }
}

/** Total amount still needed across all goal-tracked categories. */
export function totalUnderfundedByGoal(month: BudgetMonth): number {
  let sum = 0
  for (const g of month.categoryGroups) {
    for (const c of g.categories) {
      if (c.targetUnderfunded && c.targetUnderfunded > 0) sum += c.targetUnderfunded
    }
  }
  return Math.round(sum * 100) / 100
}

/**
 * Compute targetUnderfunded from the goal config + current category
 * state. Mirrors YNAB's goal_under_funded but only for the goal types
 * we model. Returns 0 when there's no goal or the user is on track.
 */
export function computeUnderfunded(c: BudgetCategory, _year: number, month: number): number {
  if (!c.goalType || c.target == null || c.target <= 0) return 0
  const round = (n: number) => Math.round(n * 100) / 100
  // Money put toward the category this month: leftover carried in plus
  // assigned. Spending doesn't re-demand funds within the month, so
  // back activity out of the balance. Works for both doc shapes
  // (imported leftover baked into available, or explicit carryOver).
  const funded = c.available - c.activity
  switch (c.goalType) {
    case 'NEED': {
      if (c.goalCadence === 'yearly') {
        // Pro-rate the annual amount: this month's share is the gap
        // divided by months remaining until the due month (inclusive).
        // No due month set → spread over a full year.
        const monthsRemaining =
          c.goalMonth != null ? ((c.goalMonth - month + 12) % 12) + 1 : 12
        return round(Math.max(0, c.target - funded) / monthsRemaining)
      }
      // Need `target` funded each month, counting what carried in.
      // (Weekly is treated monthly for the v1 calc — follow-up.)
      return round(Math.max(0, c.target - funded))
    }
    case 'MF': {
      // Set aside `monthlyContribution` (default to target) each month
      // until the balance reaches `target`.
      const remaining = Math.max(0, c.target - c.available)
      const monthly = c.goalMonthlyContribution ?? c.target
      return round(Math.min(Math.max(0, monthly - c.assigned), remaining))
    }
    case 'TB': {
      // Targeted balance: bring `available` up to `target`.
      return round(Math.max(0, c.target - c.available))
    }
    case 'DEBT': {
      // Same shape as MF — a monthly chunk toward the balance.
      const monthly = c.goalMonthlyContribution ?? c.target
      return round(Math.max(0, monthly - c.assigned))
    }
  }
}

export interface GoalFields {
  goalType: BudgetCategory['goalType']
  target: BudgetCategory['target']
  goalCadence: BudgetCategory['goalCadence']
  goalDay: BudgetCategory['goalDay']
  goalMonth: BudgetCategory['goalMonth']
  goalMonthlyContribution: BudgetCategory['goalMonthlyContribution']
  targetMonth: BudgetCategory['targetMonth']
  note: BudgetCategory['note']
}

/**
 * Update one category's goal config, re-derive `targetUnderfunded`,
 * and return a new BudgetMonth. Use for saving the drawer.
 */
export function updateCategoryGoal(
  month: BudgetMonth,
  categoryId: string,
  fields: GoalFields,
): CalcResult<BudgetMonth> {
  const found = findCategory(month, categoryId)
  if (!found) return { ok: false, error: { kind: 'categoryNotFound', id: categoryId } }
  const merged: BudgetCategory = { ...found, ...fields }
  const underfunded = computeUnderfunded(merged, month.year, month.month)
  const groups: CategoryGroup[] = month.categoryGroups.map((g) => ({
    ...g,
    categories: g.categories.map((c) =>
      c.id === categoryId
        ? { ...merged, targetUnderfunded: underfunded }
        : c,
    ),
  }))
  return { ok: true, value: { ...month, categoryGroups: groups } }
}

/**
 * Top up every underfunded-by-goal category in one pass. Returns the
 * new BudgetMonth with `assigned += targetUnderfunded` for each row
 * that has a positive underfunded amount. No-op categories pass
 * through unchanged.
 */
export function fundAllUnderfunded(month: BudgetMonth): BudgetMonth {
  const groups: CategoryGroup[] = month.categoryGroups.map((g) => ({
    ...g,
    categories: g.categories.map((c) => {
      if (!c.targetUnderfunded || c.targetUnderfunded <= 0) return c
      const newAssigned = Math.round((c.assigned + c.targetUnderfunded) * 100) / 100
      // By definition we just covered the gap — drop targetUnderfunded
      // to 0 so the "Fund all" button stops firing on repeat clicks.
      return {
        ...c,
        assigned: newAssigned,
        available: Math.round((c.available + (newAssigned - c.assigned)) * 100) / 100,
        targetUnderfunded: 0,
      }
    }),
  }))
  const totalAssigned = groups.reduce(
    (acc, g) => acc + g.categories.reduce((a, c) => a + c.assigned, 0),
    0,
  )
  return {
    ...month,
    categoryGroups: groups,
    budgeted: totalAssigned,
    available: readyToAssign(month.carryOver ?? 0, month.income, totalAssigned),
  }
}
